/**
 * The bell, its badge, and the tray behind it.
 *
 * **Snapshot then socket, never polled.** `Runtime` seeds the store from
 * `GET /notifications` at cold load and the `notifications` channel keeps it
 * current — one of the six private channels, subscribed since Phase 6 and until
 * now rendered nowhere. `docs/03` Appendix B still lists three network timers;
 * this adds none.
 *
 * **The unread count is derived from the store, not from the endpoint's
 * `unread_count`.** The endpoint's figure is correct at the instant it answered
 * and a settlement arrives every sixty seconds; the badge has to move when the
 * frame does. `version` is what makes the derivation re-run — `LogStore.append`
 * mutates in place, so the rows array alone memoises an empty list forever
 * (invariant 17, §F25k).
 *
 * **Marking read is one request per tray-open**, never one per item
 * (`openapi`: "batched"). An empty id list marks everything, which is what the
 * server offers and what avoids racing the arrival of a new one.
 *
 * This subscribes the store itself. `<Header>` renders on every route, so a
 * subscription hoisted into it would re-render the whole shell on every
 * notification (invariant 2).
 */

import { memo, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Pill } from '@/components/primitives'
import { useNotifications, useRuntime } from '@/app/use-runtime'
import { cx } from '@/lib/cx'
import { formatUtcTime } from '@/lib/format'

interface Notification extends Record<string, unknown> {
  notification_id?: string
  type?: string
  severity?: string
  title?: string
  body?: string
  deep_link?: string | null
  created_at_ms?: number
  read?: boolean
}

const TONE: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'neutral'> = {
  SUCCESS: 'success',
  WARNING: 'warning',
  ERROR: 'danger',
  CRITICAL: 'danger',
  INFO: 'info',
}

export const NotificationTray = memo(function NotificationTray() {
  const runtime = useRuntime()
  const { rows, version } = useNotifications<Notification>()
  const [open, setOpen] = useState(false)

  /**
   * When the tray was last opened, in **server-payload time**.
   *
   * The server marks the rows read, and nothing tells this client about it: a
   * `read` flag flipping is not a state change worth a frame on a channel that
   * carries settlements, and re-fetching the list to learn it would be a request
   * per tray-open for a boolean the client already knows. So the badge subtracts
   * what this tab has shown the user, and the server call is the durable half —
   * which is what makes the count right after a reload, on any device.
   *
   * A **timestamp**, not a set of ids: a notification that arrives while the
   * tray is open is genuinely unread, and a set captured at open would swallow
   * it. Compared against `created_at_ms`, which is the venue's clock and the
   * same one the rows are sorted by (invariant 7 — no `Date.now()` here).
   */
  const [shownThroughMs, setShownThroughMs] = useState(0)

  const { sorted, unread } = useMemo(() => {
    const list = [...rows].sort((a, b) => (b.created_at_ms ?? 0) - (a.created_at_ms ?? 0))
    return {
      sorted: list,
      unread: list.filter((n) => n.read !== true && (n.created_at_ms ?? 0) > shownThroughMs).length,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, version, shownThroughMs])

  const toggle = () => {
    const next = !open
    setOpen(next)
    // On open, and only if there is something to mark. A request that marks
    // nothing is a request nobody asked for, sixty times an hour.
    if (next && unread > 0) {
      setShownThroughMs(sorted[0]?.created_at_ms ?? shownThroughMs)
      void runtime.api.notifications
        .markNotificationsRead({ notification_ids: [] })
        .catch(() => {
          // A failed mark leaves the *server* thinking they are unread, which is
          // the safe direction: an unread notification shown twice on the next
          // device costs a glance, and one silently marked read costs the thing
          // it was telling them.
        })
    }
  }

  return (
    <div className="relative">
      <button
        type="button"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
        data-testid="notification-bell"
        data-unread={unread}
        onClick={toggle}
        className={cx(
          'flex h-6 items-center gap-1 rounded-sm px-1_5 text-body',
          // components.md §1's hit-target floor, below 768px only. `h-6` stays
          // the drawn height; `min-h`/`min-w` grow only the pressable box.
          'justify-center min-h-[var(--ud-tap-min)] min-w-[var(--ud-tap-min)] md:min-h-0 md:min-w-0',
          'text-text-secondary hover:text-text-primary',
          'focus-visible:shadow-focus focus-visible:outline-none',
          // **Above the click-away overlay.** Without this the overlay covers
          // the bell while the tray is open, so clicking it lands on the
          // dismisser instead of the toggle — the tray closes and immediately
          // has nothing to reopen it. Keyboard users hit the same wall: the
          // button is there, focusable, and unclickable.
          'relative z-30',
        )}
      >
        {/*
          A bell, drawn rather than typed. The six glyphs this product ships live
          in the mono face because a 2.6 KB patch file puts them there (§F17);
          adding a seventh would mean re-subsetting a font for one control.
        */}
        <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" aria-hidden="true">
          <path
            d="M8 2a4 4 0 0 0-4 4v2.6L2.8 11h10.4L12 8.6V6a4 4 0 0 0-4-4Z"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinejoin="round"
          />
          <path d="M6.4 13a1.6 1.6 0 0 0 3.2 0" stroke="currentColor" strokeWidth="1.2" />
        </svg>
        {unread > 0 ? (
          // The `data-*` goes on a wrapper: `<Pill>` destructures its known
          // props and does not spread the rest, so an attribute passed to it is
          // silently dropped.
          <span data-testid="notification-badge">
            <Pill tone="accent">{unread > 99 ? '99+' : unread}</Pill>
          </span>
        ) : null}
      </button>

      {open ? (
        <>
          {/* A click anywhere else closes it. No scrim colour — this is a
              popover, not a modal, and dimming the page for a tray would say
              the page is unavailable, which it is not. */}
          <button
            type="button"
            aria-label="Close notifications"
            className="fixed inset-0 z-10 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div
            data-testid="notification-tray"
            className={cx(
              // **Below 768px, the screen's width rather than the bell's edge.**
              // Anchored `right-0` to the bell, a 352px tray hangs off the
              // *left* of a phone, because the account chip and the menu button
              // sit to the bell's right — and a `scrollWidth` check cannot see
              // overflow in that direction (`e2e/responsive.spec.ts`).
              'fixed inset-x-2 top-[calc(var(--ud-header-height)+var(--ud-space-1))] z-20',
              'md:absolute md:inset-x-auto md:right-0 md:top-[calc(100%+var(--ud-space-1))] md:w-[calc(352*var(--ud-u))]',
              'ud-scroll max-h-[calc(384*var(--ud-u))] rounded-lg bg-bg-surface shadow-popover',
              'border border-border-default',
            )}
          >
            {sorted.length === 0 ? (
              <p className="px-3 py-4 text-center text-body-sm text-text-tertiary">
                Nothing yet. Settlements and expiries land here.
              </p>
            ) : (
              <ul className="flex flex-col">
                {sorted.map((n) => (
                  <li
                    key={String(n.notification_id)}
                    data-read={n.read === true ? 'true' : 'false'}
                    className="border-b border-border-subtle px-3 py-2 last:border-b-0"
                  >
                    <div className="flex items-baseline gap-2">
                      <Pill tone={TONE[String(n.severity)] ?? 'neutral'}>{String(n.type ?? '—')}</Pill>
                      <span className="ml-auto text-caption text-text-tertiary">
                        {typeof n.created_at_ms === 'number' ? formatUtcTime(n.created_at_ms) : ''}
                      </span>
                    </div>
                    <p className="mt-1 text-body-sm text-text-primary">{n.title ?? '—'}</p>
                    {n.body ? <p className="text-caption text-text-secondary">{n.body}</p> : null}
                    {typeof n.deep_link === 'string' && n.deep_link.startsWith('/') ? (
                      // **Only a same-origin path.** `deep_link` is a server
                      // field and rendering it as an anchor without checking
                      // would let a payload navigate a signed-in user anywhere.
                      <Link
                        to={n.deep_link}
                        onClick={() => setOpen(false)}
                        className="text-caption text-text-link hover:underline"
                      >
                        Open the market
                      </Link>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      ) : null}
    </div>
  )
})
